/// Equirectangular projection with optional magnetic-variation rotation.
/// Direct port of FlatoutATC/Support/CoordinateProjector.swift.
///
/// The web canvas uses Pixi's standard Y-down screen space (matches
/// SwiftUI's `yFlipped: true`), so projected Y has the standard "up is
/// negative" convention.

import type { GeoCoordinate } from '../types/LevelData'

const EARTH_RADIUS_NM = 3440.065

export interface ProjectorOptions {
  worldCenter: GeoCoordinate
  worldSizeInNM: number
  /// World-space frame size. The width is what `worldSizeInNM` maps to.
  width: number
  height: number
  magneticVariationDegrees: number
}

/// Pure projection: lat/lon → world-space CGPoint-equivalent.
/// Origin (0,0) is the top-left of the world frame; the projector centers
/// the projection on `worldCenter`.
export class CoordinateProjector {
  private readonly center: GeoCoordinate
  private readonly width: number
  private readonly height: number
  private readonly magVarRad: number
  private readonly scale: number // pixels per NM

  constructor(opts: ProjectorOptions) {
    this.center = opts.worldCenter
    this.width = opts.width
    this.height = opts.height
    this.magVarRad = (opts.magneticVariationDegrees * Math.PI) / 180
    this.scale = opts.width / opts.worldSizeInNM
  }

  /// Project a geographic coordinate into world-space pixels.
  /// `applyRotation` rotates around the world center by the magnetic
  /// variation, producing a "magnetic north up" view.
  project(coord: GeoCoordinate, applyRotation: boolean): { x: number; y: number } {
    const centerLatRad = (this.center.latitude * Math.PI) / 180
    const latRad = (coord.latitude * Math.PI) / 180
    const lonRad = (coord.longitude * Math.PI) / 180

    const yInNM = (latRad - centerLatRad) * EARTH_RADIUS_NM
    const xInNM =
      (lonRad - (this.center.longitude * Math.PI) / 180) *
      Math.cos(centerLatRad) *
      EARTH_RADIUS_NM

    let xPx = xInNM * this.scale
    let yPx = yInNM * this.scale

    if (applyRotation) {
      const cosM = Math.cos(this.magVarRad)
      const sinM = Math.sin(this.magVarRad)
      const rx = xPx * cosM - yPx * sinM
      const ry = xPx * sinM + yPx * cosM
      xPx = rx
      yPx = ry
    }

    // Y-flipped: screen Y grows downward, so subtract.
    return { x: this.width / 2 + xPx, y: this.height / 2 - yPx }
  }
}
